// Data Caching Utility for CryptoWeb
// Improves performance by caching API responses

class DataCache {
  constructor() {
    this.cache = new Map();
    this.timestamps = new Map();
    this.defaultTTL = 30000; // 30 seconds default TTL
  }

  // Set cache entry with TTL
  set(key, data, ttl = this.defaultTTL) {
    this.cache.set(key, data);
    this.timestamps.set(key, Date.now() + ttl);
    
    // Clean up expired entries periodically
    this.cleanup();
  }

  // Get cache entry if not expired
  get(key) {
    const data = this.cache.get(key);
    const expiry = this.timestamps.get(key);
    
    if (!data || !expiry) {
      return null;
    }
    
    if (Date.now() > expiry) {
      this.delete(key);
      return null;
    }
    
    return data;
  }

  // Delete cache entry
  delete(key) {
    this.cache.delete(key);
    this.timestamps.delete(key);
  }

  // Check if key exists and is not expired
  has(key) {
    return this.get(key) !== null;
  }

  // Clear all cache
  clear() {
    this.cache.clear();
    this.timestamps.clear();
  }

  // Clean up expired entries
  cleanup() {
    const now = Date.now();
    for (const [key, expiry] of this.timestamps) {
      if (now > expiry) {
        this.delete(key);
      }
    }
  }

  // Get cache stats
  getStats() {
    return {
      size: this.cache.size,
      entries: Array.from(this.cache.keys()),
      memoryUsage: this.estimateMemoryUsage()
    };
  }

  // Estimate memory usage (rough calculation)
  estimateMemoryUsage() {
    let totalSize = 0;
    for (const [key, value] of this.cache) {
      totalSize += JSON.stringify({ key, value }).length * 2; // rough estimate
    }
    return `${(totalSize / 1024).toFixed(2)} KB`;
  }
}

// Create global cache instance
const dataCache = new DataCache();

export default dataCache;

// Cached fetch wrapper
export const cachedFetch = async (url, options = {}, ttl = 30000) => {
  const cacheKey = `${url}${JSON.stringify(options)}`;
  
  // Check cache first
  const cachedData = dataCache.get(cacheKey);
  if (cachedData) {
    console.log(`📦 Cache hit for ${url}`);
    return cachedData;
  }
  
  // Fetch from network
  console.log(`🌐 Cache miss, fetching ${url}`);
  const response = await fetch(url, options);
  const data = await response.json();
  
  // Cache the response
  dataCache.set(cacheKey, data, ttl);
  
  return data;
};

// Clear cache periodically (every 5 minutes)
setInterval(() => {
  dataCache.cleanup();
}, 5 * 60 * 1000);